import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, ArrowRightLeft, History, ListChecks, ArrowUp, ArrowDown } from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const fetchOtherScans = async (scanId) => {
    const response = await fetch(ApiNames.AI_HUB_PAST_SCANS_URL_BY_SCAN_ID(scanId), { method: 'POST', credentials: 'include' });
    const json = await response.json();
    return [json.filter(item => item.scanId != scanId), json.find(item => item.scanId == scanId)];
};

const WhatChangedTab = ({ scanId }) => {
    const [otherScans, setOtherScans] = useState([]);
    const [selectedOtherScan, setSelectedOtherScan] = useState(null);
    const [changesData, setChangesData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cached, setCached] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [tokensUsed, setTokensUsed] = useState(null);
    const [costUsd, setCostUsd] = useState(null);
    const [currentScanDate, setCurrentScanDate] = useState('CURRENT_SCAN_DATE');

    // Fetch allowable other scans on mount
    useEffect(() => {
        const loadOtherScans = async () => {
            try {
                const [scans, currentScan] = await fetchOtherScans(scanId);
                setOtherScans(scans);
                setCurrentScanDate(currentScan.date);
                if (scans.length > 0) setSelectedOtherScan(scans[0]);
            } catch (e) {
                setOtherScans([]);
            }
        };
        loadOtherScans();
    }, [scanId]);

    // Fetch cached data if possible when scanId or selectedOtherScan changes
    useEffect(() => {
        if (!selectedOtherScan) return;
        const fetchCached = async () => {
            setLoading(true);
            setError(null);
            setCached(false);
            try {
                const cachedResp = await fetch(
                    ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, 'What Changed') + `?otherScanId=${selectedOtherScan.scanId}`,
                    { method: 'POST', credentials: 'include' }
                );
                if (cachedResp.status === 200) {
                    const cachedJson = await cachedResp.json();
                    if (cachedJson && cachedJson.response) {
                        const parsed = JSON.parse(cachedJson.response);
                        setChangesData(parsed);
                        setCached(true);
                        setLastUpdated(cachedJson.lastUpdated || null);
                        setTokensUsed(cachedJson.tokens_used || null);
                        setCostUsd(cachedJson.cost_usd || null);
                        setLoading(false);
                        return;
                    }
                }
                setChangesData(null);
                setCached(false);
            } catch (err) {
                setChangesData(null);
                setCached(false);
            } finally {
                setLoading(false);
            }
        };
        fetchCached();
    }, [scanId, selectedOtherScan]);

    const handleGenerate = async () => {
        if (!selectedOtherScan) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                ApiNames.AI_HUB_WHAT_CHANGED(scanId, selectedOtherScan.scanId),
                { method: 'POST', credentials: 'include' }
            );
            if (!response.ok) throw new Error('Failed to fetch What Changed data');
            const json = await response.json();
            const parsed = JSON.parse(json.response);
            setChangesData(parsed);
            setCached(false);
            setLastUpdated(json.lastUpdated || null);
            setTokensUsed(json.tokens_used || null);
            setCostUsd(json.cost_usd || null);
        } catch (err) {
            setError('Failed to load What Changed data.');
        } finally {
            setLoading(false);
        }
    };

    // Helper to format scan dropdown label
    const formatScanLabel = (otherScan, currentScanId, currentScanDate, otherScanDate) => {
        return `${new Date(otherScanDate).toString()} (${otherScan.scanId}) -> ${new Date(currentScanDate).toString()} (${currentScanId})`;
    };

    return (
        <div className="flex flex-col items-center gap-8">
            <div className="w-full max-w-6xl">
                <div className="card-header flex items-center justify-between mb-2">
                    <div className="card-title flex items-center gap-2">
                        <History className="w-6 h-6 text-blue-400" />
                        <h3 className="font-bold text-lg">AI "What Changed?" Engine</h3>
                    </div>
                    <span className="card-badge flex items-center gap-1 bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold">
                        <ArrowRightLeft className="w-4 h-4 mr-1" />Compare Scans
                    </span>
                </div>
                <p className="text-gray-400 mb-6 text-sm mt-2">
                    Analyze and explain what changed between two compliance scans. Select a previous scan to compare with the current one.
                </p>

                {/* Scan selection dropdown */}
                <div className="flex flex-row items-center gap-4 mb-6">
                    <label className="font-semibold text-sm text-gray-700">Compare:</label>
                    <select
                        className="border border-base-300 rounded-lg px-4 py-2 bg-base-50 text-base-content focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={selectedOtherScan?.scanId || ''}
                        onChange={e => {
                            const found = otherScans.find(s => s.scanId === e.target.value);
                            setSelectedOtherScan(found || null);
                        }}
                        disabled={loading || otherScans.length === 0}
                    >
                        {otherScans.length === 0 ? (
                            <option value="">No previous scans available</option>
                        ) : (
                            otherScans.map(scan => (
                                <option key={scan.scanId} value={scan.scanId}>
                                    {formatScanLabel(scan, scanId, currentScanDate, scan.date)}
                                </option>
                            ))
                        )}
                    </select>
                </div>

                {/* Main Content */}
                {loading && (
                    <div className="ai-response flex flex-col items-center justify-center py-12 animate-fadeIn">
                        <Loader2 className="animate-spin text-blue-400 w-10 h-10 mb-4" />
                        <span className="text-gray-400">Generating AI analysis...</span>
                    </div>
                )}
                {!loading && error && (
                    <div className="ai-response text-center text-red-400 py-12">{error}</div>
                )}
                {!loading && !error && changesData && (
                    <>
                        <div className="ai-response bg-base-100 border border-base-300 rounded-lg p-4 animate-fadeIn mb-2">
                            {/* <div className="grid grid-cols-[2fr_1fr_3fr_1fr_2fr] gap-3 mb-2 px-1">
                                <h4 className="font-bold text-sm flex items-center gap-2">
                                    <ListChecks className="w-4 h-4 text-blue-400" />Change
                                </h4>
                                <h4 className="font-bold text-sm flex items-center gap-2">Value</h4>
                                <h4 className="font-bold text-sm flex items-center gap-2">Reason</h4>
                                <h4 className="font-bold text-sm flex items-center gap-2">Owner</h4>
                                <h4 className="font-bold text-sm flex items-center gap-2">Triggered By</h4>
                            </div> */}
                            {/* Main Changes List */}
                            <div className="grid grid-cols-1 gap-4 mb-6">
                                {changesData.Changes?.length > 0 ? (
                                    changesData.Changes.map((item, idx) => {
                                        const numericValue = parseFloat(item['Change Value']);
                                        const isNegative = numericValue < 0;

                                        return (
                                            <div key={idx} className="border border-base-300 bg-base-50 p-5 rounded-lg shadow-sm flex flex-col">
                                                {/* Header Row: Icon + Title (Left) and Badge (Right) */}
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="flex items-center gap-2">
                                                        {isNegative ? (
                                                            <ArrowDown className="w-5 h-5 text-red-600" />
                                                        ) : (
                                                            <ArrowUp className="w-5 h-5 text-green-600" />
                                                        )}
                                                        <h3 className="font-bold text-lg text-base-content leading-none">
                                                            {item.Change}: {item['Change Value']}%
                                                        </h3>
                                                    </div>

                                                    {/* Right-aligned Badge */}
                                                    <span
                                                        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${isNegative
                                                                ? 'bg-red-100 text-red-600'
                                                                : 'bg-green-100 text-green-600'
                                                            }`}
                                                    >
                                                        {item['Change Value']}% {isNegative ? 'Decrease' : 'Improvement'}
                                                    </span>
                                                </div>

                                                {/* Reason Section */}
                                                <p className="text-sm text-base-content/90 mb-0">
                                                    <span className="font-bold">Why it changed: </span>
                                                    {item.Reason}
                                                </p>

                                                {/* Separator Line */}
                                                <div className="border-t border-base-300 my-3"></div>

                                                {/* Footer: Owner and Trigger */}
                                                <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs text-base-content/70">
                                                    <div>
                                                        <span className="font-semibold text-base-content/50 uppercase tracking-wider mr-1">
                                                            Owner:
                                                        </span>
                                                        {item.Owner}
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold text-base-content/50 uppercase tracking-wider mr-1">
                                                            Triggered by:
                                                        </span>
                                                        {item['Triggered by']}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center text-gray-400 py-8 border border-dashed border-base-300 rounded-lg">
                                        No changes detected between these scans.
                                    </div>
                                )}
                            </div>

                            {/* Metadata footer */}
                            <div className="mt-6 pt-3 border-t border-base-200 text-[10px] text-gray-400 flex flex-wrap gap-4">
                                {tokensUsed && <span>Tokens used: {tokensUsed}. Cost: ${costUsd}</span>}
                                {cached && lastUpdated && (
                                    <span>Viewing Cached What Changed analysis. Last updated: {lastUpdated}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-row justify-center gap-4 mb-4">
                            <button
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150 flex items-center gap-2"
                                onClick={handleGenerate}
                                disabled={loading || !scanId || !selectedOtherScan}
                            >
                                <RefreshCw className="w-5 h-5" /> Regenerate Analysis
                            </button>
                        </div>
                    </>
                )}
                {!loading && !error && !changesData && !cached && (
                    <div className="flex flex-row justify-center gap-4 mb-4">
                        <button
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow transition-all duration-150 flex items-center gap-2"
                            onClick={handleGenerate}
                            disabled={loading || !scanId || !selectedOtherScan}
                        >
                            <RefreshCw className="w-5 h-5" /> Analyze Changes
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WhatChangedTab;
